import React from "react";
import type { FilterableColumn, FilterChip } from "../../hooks/useTransactionFilters";
import { BUCKETS, type OutcomeBucketId } from "../../lib/outcomeBuckets";

// =============================================================================
// FILTER BAR
// =============================================================================
// PURPOSE: Horizontal bar of filter chips for click-to-filter tables.
// Generic: works with Transaction Log, Installment Tracking, or any table
// that uses the same chip pattern ({ column: string, value: string }).
//
// REQUIREMENTS:
// - Only renders when at least one filter chip is active (chips.length > 0)
// - Groups chips by column with a column label, chips, and a per-column × clear
// - "Reset filters" clears all active exclusions at once
// - Left-clicking a chip calls onRemoveFilter to add that value back
// - Follows liquid glass visual theme (liquid-glass-light, text-glass-* classes)
// =============================================================================

// -----------------------------------------------------------------------------
// Props — generic over column type for reuse across pages
// -----------------------------------------------------------------------------

interface FilterBarProps<C extends string = FilterableColumn> {
  chips: { column: C; value: string }[];
  onRemoveFilter: (column: C, value: string) => void;
  onClearColumn: (column: C) => void;
  onClearAll: () => void;
}

// Re-export for backward compatibility
export type { FilterChip };

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/** Capitalize the first letter of each word for a column label. */
function formatColumnLabel(column: string): string {
  // The `bucket` column filters outcomes grouped into buckets — Mako thinks of
  // it as the outcome, so label it that way (not "Bucket").
  if (column === "bucket") return "Outcome";
  return column.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Format chip display value — dates become "Feb 23", buckets become "No Show", everything else stays as-is. */
function formatChipValue(column: string, value: string): string {
  if (column === "bucket") {
    const bucket = BUCKETS[value as OutcomeBucketId];
    if (!bucket) return value;
    // BUCKETS labels are UPPERCASE ("NO SHOW") — title-case each word for the chip.
    return bucket.label
      .split(" ")
      .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
      .join(" ");
  }
  if (column !== "date") return value;
  // value is YYYY-MM-DD — format as "Mon DD"
  const d = new Date(value + "T00:00:00");
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

// Use a function component with explicit generic — React.FC doesn't support generics
function FilterBarInner<C extends string>({
  chips,
  onRemoveFilter,
  onClearColumn,
  onClearAll,
}: FilterBarProps<C>) {
  // Don't render anything when no filters are active
  if (chips.length === 0) return null;

  // Group chips by column, preserving insertion order
  const groupOrder: C[] = [];
  const groups: Partial<Record<string, { column: C; value: string }[]>> = {};

  for (const chip of chips) {
    if (!groups[chip.column as string]) {
      groupOrder.push(chip.column);
      groups[chip.column as string] = [];
    }
    groups[chip.column as string]!.push(chip);
  }

  return (
    <div className="liquid-glass-light px-4 py-2 flex flex-wrap items-center gap-3">
      {groupOrder.map((column, groupIndex) => (
        <React.Fragment key={column}>
          {/* Visual separator between groups */}
          {groupIndex > 0 && (
            <span className="text-black/20 select-none" aria-hidden="true">
              |
            </span>
          )}

          {/* Column group */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Column label */}
            <span className="text-xs uppercase tracking-wider font-semibold text-glass-muted whitespace-nowrap">
              {formatColumnLabel(column)}:
            </span>

            {/* Excluded-value chips */}
            {(groups[column as string] ?? []).map((chip) => (
              <button
                key={`${chip.column}::${chip.value}`}
                type="button"
                data-testid="filter-chip"
                onClick={() => onRemoveFilter(chip.column, chip.value)}
                title={`Add "${formatChipValue(chip.column, chip.value)}" back to table`}
                className="
                  inline-flex items-center
                  bg-black/[0.05] hover:bg-black/[0.08]
                  text-glass-secondary
                  text-xs font-medium
                  rounded-full px-2.5 py-0.5
                  transition-colors duration-150
                  cursor-pointer select-none
                  anim-tap
                "
              >
                {formatChipValue(chip.column, chip.value)}
              </button>
            ))}

            {/* Per-column clear button */}
            <button
              type="button"
              onClick={() => onClearColumn(column)}
              title={`Clear all ${formatColumnLabel(column)} filters`}
              className="
                text-glass-muted hover:text-glass-secondary
                text-xs leading-none
                transition-colors duration-150
                cursor-pointer select-none ml-0.5
              "
              aria-label={`Clear ${formatColumnLabel(column)} filters`}
            >
              ×
            </button>
          </div>
        </React.Fragment>
      ))}

      {/* Reset all button — always at the far right */}
      <button
        type="button"
        onClick={onClearAll}
        className="
          ml-auto
          text-xs font-medium
          text-ds-accent hover:opacity-80
          transition-colors duration-150
          cursor-pointer select-none whitespace-nowrap
          anim-tap
        "
      >
        Reset filters
      </button>
    </div>
  );
}

// Wrap in a named component for React DevTools + default export
const FilterBar = FilterBarInner as <C extends string>(
  props: FilterBarProps<C>
) => React.ReactElement | null;
export default FilterBar;
